import { useMemo, useState } from 'react';
import type { Progression } from '@theory/progressions';
import type { Key } from '@theory/keys';
import { resolveProgression, getProgressionTitle } from '@util/musicTheory';
import { formatProgressionExample } from '@util/progressions';
import { ChordChip } from './ChordChip';
import styles from './ProgressionCard.module.css';

interface ProgressionCardProps {
  progression: Progression;
  scaleKey: Key;
}

export function ProgressionCard({ progression, scaleKey }: ProgressionCardProps) {
  const [open, setOpen] = useState(false);

  const resolved = useMemo(
    () => resolveProgression(progression.steps, scaleKey),
    [progression, scaleKey],
  );
  const title = useMemo(() => getProgressionTitle(progression, scaleKey), [progression, scaleKey]);
  const numeralString = useMemo(() => resolved.map((r) => r.numeral).join('–'), [resolved]);
  const showSubtitle = numeralString !== title;
  const hasChromatic = resolved.some((r) => r.isChromatic);

  return (
    <article className={[styles.card, open && styles.open].filter(Boolean).join(' ')}>
      <button type="button" className={styles.header} onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        <span className={styles.name}>{title}</span>
        {showSubtitle && <span className={styles.numerals}>{numeralString}</span>}
      </button>
      <div className={styles.collapse}>
        <div className={styles.inner}>
          {progression.description && <p className={styles.description}>{progression.description}</p>}
          <div className={styles.grid}>
            {resolved.map((r, i) => (
              <ChordChip key={i} chordId={r.chordId} chromatic={r.isChromatic} />
            ))}
          </div>
          {hasChromatic && (
            <p className={styles.borrowedNote}>Highlighted chords are borrowed (chromatic) — outside the diatonic set.</p>
          )}
          {progression.examples.length > 0 && (
            <div className={styles.examples}>
              <span className={styles.examplesLabel}>Heard in</span>
              <ul className={styles.examplesList}>
                {progression.examples.map((ex, i) => (
                  <li key={i}>{formatProgressionExample(ex.title, ex.artist, ex.year)}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </article>
  );
}
